import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { apiFetch } from '@/api/client';
import {
  ANALYTICS_METRIC_KEYS,
  ANALYTICS_METRIC_LABELS,
  type IntegrationPublic,
} from '@marketing-hub/shared';
import { useProjectContext } from '@/project/ProjectContext';
import { ProjectGate } from '@/features/projects/ProjectGate';
import { Button } from '@/components/ui/Button';
import { Field, SelectInput, TextInput } from '@/components/ui/Field';
import { ErrorState, LoadingState } from '@/components/StateMessage/StateMessage';
import { kpiRangeForDays, formatPercent } from '@/features/dashboard/kpiRollup';
import { AttributionBars } from '@/features/analytics/AttributionBars';
import { EmptyPanel } from '@/features/analytics/EmptyPanel';
import {
  evaluateGoal,
  formatEuro,
  goalStatusLabel,
  openPipelineValue,
} from '@/features/analytics/goalMetrics';
import {
  ANALYTICS_SECTIONS,
  aggregateWinRate,
  buildFunnelSteps,
  funnelConversions,
  largestDropOff,
} from '@/features/analytics/analyticsSections';
import { scrollToSection, useScrollSpy, useSectionInView } from '@/features/analytics/useSectionInView';
import {
  SortableTable,
  compareSort,
  toggleSort,
  type SortState,
} from '@/features/analytics/SortableTable';
import tableStyles from '@/features/analytics/SortableTable.module.css';
import { SummaryMetricCard } from '@/features/analytics/SummaryMetricCard';
import { AnalyticsCsvExport } from '@/features/analytics/AnalyticsCsvExport';
import { AnalyticsGooglePanel } from '@/features/analytics/AnalyticsGooglePanel';
import styles from './AnalyticsPage.module.css';

type Funnel = {
  new_c: number;
  contacted_c: number;
  replied_c: number;
  meeting_c: number;
  customer_c: number;
  rejected_c: number;
  deals_won: number;
};

type CampSortKey = 'name' | 'reply_rate' | 'enrolled' | 'sent';
type PipeSortKey = 'source' | 'win_rate' | 'won';

function goalRowClass(status: ReturnType<typeof evaluateGoal>): string {
  if (status === 'ok') return styles.goalRowOk;
  if (status === 'warn') return styles.goalRowWarn;
  if (status === 'bad') return styles.goalRowBad;
  return '';
}

export function AnalyticsPage() {
  const { selectedProject } = useProjectContext();
  const projectId = selectedProject?.id;
  const qc = useQueryClient();
  const [rangeDays, setRangeDays] = useState<7 | 30 | 90>(30);
  const { from, to } = kpiRangeForDays(rangeDays);

  const [goalMetric, setGoalMetric] =
    useState<(typeof ANALYTICS_METRIC_KEYS)[number]>('reply_rate');
  const [goalTarget, setGoalTarget] = useState('0.05');

  const [campSort, setCampSort] = useState<SortState<CampSortKey>>({
    key: 'reply_rate',
    dir: 'desc',
  });
  const [pipeSort, setPipeSort] = useState<SortState<PipeSortKey>>({
    key: 'win_rate',
    dir: 'desc',
  });

  const rangeLabel = useMemo(() => {
    const f = new Date(from);
    const t = new Date(to);
    return `${f.toLocaleDateString('nl-NL')} – ${t.toLocaleDateString('nl-NL')}`;
  }, [from, to]);

  const sectionIds = useMemo(() => ANALYTICS_SECTIONS.map((s) => s.id), []);
  const activeSection = useScrollSpy(sectionIds);

  useEffect(() => {
    const hash = window.location.hash.replace(/^#/, '');
    if (hash && sectionIds.includes(hash)) {
      window.requestAnimationFrame(() => scrollToSection(hash));
    }
  }, [sectionIds]);

  const integrationsQ = useQuery({
    queryKey: ['integrations', projectId],
    enabled: Boolean(projectId),
    queryFn: () =>
      apiFetch<{ integrations: IntegrationPublic[] }>(
        `/api/integrations?project_id=${encodeURIComponent(projectId!)}`,
      ),
  });

  const executiveQ = useQuery({
    queryKey: ['analytics-executive', projectId, from, to],
    enabled: Boolean(projectId),
    queryFn: () => {
      const qs = new URLSearchParams({ project_id: projectId!, from, to });
      return apiFetch<{
        executive: {
          kpi: {
            reply_rate: number;
            bounce_rate: number;
            mails_sent: number;
          };
        };
      }>(`/api/analytics/executive?${qs.toString()}`);
    },
  });

  const funnelQ = useQuery({
    queryKey: ['analytics-funnel', projectId, from, to],
    enabled: Boolean(projectId),
    queryFn: () => {
      const qs = new URLSearchParams({ project_id: projectId!, from, to });
      return apiFetch<{ funnel: Funnel }>(`/api/analytics/funnel?${qs.toString()}`);
    },
  });

  const campagnesView = useSectionInView();
  const pipelineView = useSectionInView();
  const attributieView = useSectionInView();
  const doelenView = useSectionInView();

  const rangeQs = useMemo(() => {
    const qs = new URLSearchParams({ project_id: projectId!, from, to });
    return qs.toString();
  }, [projectId, from, to]);

  const leaderboardQ = useQuery({
    queryKey: ['analytics-leaderboard', projectId, from, to],
    enabled: Boolean(projectId) && campagnesView.inView,
    queryFn: () =>
      apiFetch<{
        leaderboard: {
          id: string;
          name: string;
          reply_rate: number;
          enrolled: number;
          sent: number;
        }[];
      }>(`/api/analytics/campaign-leaderboard?${rangeQs}`),
  });

  const pipelineQ = useQuery({
    queryKey: ['analytics-pipeline', projectId, from, to],
    enabled: Boolean(projectId),
    queryFn: () =>
      apiFetch<{
        pipeline: {
          byOutcome: { outcome: string; deal_count: number; total_amount: string }[];
          winRateBySource: { source: string; win_rate: number; won: number; total: number }[];
          medianSalesCycleDays: number | null;
        };
      }>(`/api/analytics/pipeline?${rangeQs}`),
  });

  const attributionQ = useQuery({
    queryKey: ['analytics-attribution', projectId, from, to],
    enabled: Boolean(projectId) && attributieView.inView,
    queryFn: () =>
      apiFetch<{
        attribution: {
          first_touch: { source: string; c: number }[];
          last_touch_won: { source: string; c: number }[];
        };
      }>(`/api/analytics/attribution-summary?${rangeQs}`),
  });

  const goalsQ = useQuery({
    queryKey: ['analytics-goals', projectId],
    enabled: Boolean(projectId),
    queryFn: () =>
      apiFetch<{
        goals: {
          id: string;
          metric_key: string;
          target_value: string;
          direction: string;
        }[];
      }>(`/api/analytics/goals?project_id=${projectId}`),
  });

  const saveGoal = useMutation({
    mutationFn: () =>
      apiFetch('/api/analytics/goals', {
        method: 'PUT',
        body: JSON.stringify({
          project_id: projectId,
          metric_key: goalMetric,
          target_value: Number(goalTarget),
          direction: goalMetric === 'bounce_rate' ? 'max' : 'min',
        }),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['analytics-goals', projectId] }),
  });

  const funnel = funnelQ.data?.funnel;
  const kpi = executiveQ.data?.executive.kpi;
  const funnelSteps = funnel ? buildFunnelSteps(funnel) : [];
  const conversions = funnelConversions(funnelSteps);
  const dropLabel = largestDropOff(conversions);
  const funnelMax = funnel?.new_c ?? 1;

  const winRate = pipelineQ.data
    ? aggregateWinRate(pipelineQ.data.pipeline.winRateBySource)
    : null;
  const pipelineOpen = pipelineQ.data
    ? openPipelineValue(pipelineQ.data.pipeline.byOutcome)
    : null;

  const goals = goalsQ.data?.goals ?? [];
  const goalFor = (key: string) => goals.find((g) => g.metric_key === key);

  const actualForMetric = (key: string): number | null => {
    switch (key) {
      case 'reply_rate':
        return kpi?.mails_sent ? kpi.reply_rate : kpi?.reply_rate ?? null;
      case 'bounce_rate':
        return kpi?.mails_sent ? kpi.bounce_rate : kpi?.bounce_rate ?? null;
      case 'win_rate':
        return winRate;
      case 'pipeline_value':
        return pipelineOpen;
      default:
        return null;
    }
  };

  const formatActual = (key: string, value: number | null): string => {
    if (value == null) return '—';
    if (key === 'pipeline_value') return formatEuro(value);
    return formatPercent(value);
  };

  const sortedCampaigns = useMemo(() => {
    const rows = [...(leaderboardQ.data?.leaderboard ?? [])];
    rows.sort((a, b) => {
      switch (campSort.key) {
        case 'name':
          return compareSort(a, b, (r) => r.name, campSort.dir);
        case 'reply_rate':
          return compareSort(a, b, (r) => r.reply_rate, campSort.dir);
        case 'enrolled':
          return compareSort(a, b, (r) => r.enrolled, campSort.dir);
        case 'sent':
          return compareSort(a, b, (r) => r.sent, campSort.dir);
        default:
          return 0;
      }
    });
    return rows;
  }, [leaderboardQ.data?.leaderboard, campSort]);

  const sortedPipeline = useMemo(() => {
    const rows = [...(pipelineQ.data?.pipeline.winRateBySource ?? [])];
    rows.sort((a, b) => {
      switch (pipeSort.key) {
        case 'source':
          return compareSort(a, b, (r) => r.source, pipeSort.dir);
        case 'win_rate':
          return compareSort(a, b, (r) => r.win_rate, pipeSort.dir);
        case 'won':
          return compareSort(a, b, (r) => r.won, pipeSort.dir);
        default:
          return 0;
      }
    });
    return rows;
  }, [pipelineQ.data?.pipeline.winRateBySource, pipeSort]);

  const summaryLoading = funnelQ.isLoading || executiveQ.isLoading;

  return (
    <ProjectGate requireSelection pageTitle="Analytics">
      {!projectId ? null : (
        <div className={styles.page}>
          <header className={styles.header}>
            <h1 className={styles.title}>Analytics</h1>
            <p className={styles.subtitle}>
              <strong>{selectedProject?.name}</strong> — CRM (leads, campagnes, pipeline) én{' '}
              <strong>Google (GA4/GSC)</strong> in één view. Mail-KPIs komen uit campagnes; website
              uit integraties.
            </p>
          </header>

          <div className={styles.stickyBar}>
            <div className={styles.stickyRow}>
              <div className={styles.periodGroup}>
                {([7, 30, 90] as const).map((d) => (
                  <Button
                    key={d}
                    variant={rangeDays === d ? 'secondary' : 'ghost'}
                    onClick={() => setRangeDays(d)}
                  >
                    {d}d
                  </Button>
                ))}
              </div>
              <div className={styles.stickyActions}>
                <span className={styles.rangeLabel}>Periode: {rangeLabel}</span>
                {projectId ? (
                  <AnalyticsCsvExport projectId={projectId} from={from} to={to} />
                ) : null}
              </div>
            </div>
            <nav className={styles.sectionNav} aria-label="Analytics secties">
              {ANALYTICS_SECTIONS.map((s) => (
                <a
                  key={s.id}
                  href={`#${s.hash}`}
                  className={`${styles.navLink} ${activeSection === s.id ? styles.navLinkActive : ''}`}
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection(s.hash);
                  }}
                >
                  {s.label}
                </a>
              ))}
            </nav>
          </div>

          <section id="overzicht" className={styles.section}>
            <div className={styles.panel}>
              <div className={styles.panelHead}>
                <div>
                  <h2 className={styles.panelTitle}>Overzicht</h2>
                  <p className={styles.panelHint}>
                    Mail &amp; CRM · laatste {rangeDays} dagen (niet GA4/GSC)
                  </p>
                </div>
              </div>
              <div className={styles.crmNotice}>
                Lege funnel of 0% reply? Dan ontbreken <strong>leads/campagnes</strong> in Hub —
                GA4/GSC staan in het blok <a href="#google">Google</a> hieronder.
              </div>
              {summaryLoading ? (
                <LoadingState />
              ) : (
                <div className={styles.summaryGrid}>
                  <SummaryMetricCard
                    label="Reply rate (mail)"
                    value={
                      kpi?.mails_sent
                        ? formatPercent(kpi.reply_rate)
                        : kpi
                          ? formatPercent(kpi.reply_rate)
                          : '—'
                    }
                    actualForGoal={actualForMetric('reply_rate')}
                    goalTarget={
                      goalFor('reply_rate') ? Number(goalFor('reply_rate')!.target_value) : null
                    }
                    goalDirection={goalFor('reply_rate')?.direction}
                    formatTarget={formatPercent}
                  />
                  <SummaryMetricCard
                    label="Bounce rate (mail)"
                    value={kpi?.mails_sent ? formatPercent(kpi.bounce_rate) : '—'}
                    actualForGoal={actualForMetric('bounce_rate')}
                    goalTarget={
                      goalFor('bounce_rate') ? Number(goalFor('bounce_rate')!.target_value) : null
                    }
                    goalDirection={goalFor('bounce_rate')?.direction ?? 'max'}
                    formatTarget={formatPercent}
                  />
                  <SummaryMetricCard
                    label="Win rate (pipeline)"
                    value={winRate != null ? formatPercent(winRate) : pipelineQ.isLoading ? '…' : '—'}
                    actualForGoal={actualForMetric('win_rate')}
                    goalTarget={
                      goalFor('win_rate') ? Number(goalFor('win_rate')!.target_value) : null
                    }
                    goalDirection={goalFor('win_rate')?.direction}
                    formatTarget={formatPercent}
                  />
                  <SummaryMetricCard
                    label="Open pipeline"
                    value={
                      pipelineOpen != null
                        ? formatEuro(pipelineOpen)
                        : pipelineQ.isLoading
                          ? '…'
                          : '—'
                    }
                    actualForGoal={actualForMetric('pipeline_value')}
                    goalTarget={
                      goalFor('pipeline_value')
                        ? Number(goalFor('pipeline_value')!.target_value)
                        : null
                    }
                    goalDirection={goalFor('pipeline_value')?.direction}
                    formatTarget={formatEuro}
                  />
                  <SummaryMetricCard
                    label="Deals gewonnen"
                    value={funnel?.deals_won != null ? String(funnel.deals_won) : '—'}
                  />
                  <SummaryMetricCard
                    label="Median sales cycle"
                    value={
                      pipelineQ.data?.pipeline.medianSalesCycleDays != null
                        ? `${pipelineQ.data.pipeline.medianSalesCycleDays.toFixed(0)} dagen`
                        : pipelineQ.isLoading
                          ? '…'
                          : '—'
                    }
                  />
                </div>
              )}
            </div>
          </section>

          <section id="google" className={styles.section}>
            <div className={styles.panel}>
              <div className={styles.panelHead}>
                <div>
                  <h2 className={styles.panelTitle}>Google (GA4 &amp; Search Console)</h2>
                  <p className={styles.panelHint}>
                    Uit integraties · zelfde periode · sync dagelijks (gisteren UTC)
                  </p>
                </div>
                <Link to="/app/integraties">Integraties</Link>
              </div>
              <AnalyticsGooglePanel
                rangeDays={rangeDays}
                integrations={integrationsQ.data?.integrations ?? []}
              />
            </div>
          </section>

          <section id="funnel" className={styles.section}>
            <div className={styles.panel}>
              <div className={styles.panelHead}>
                <div>
                  <h2 className={styles.panelTitle}>Funnel</h2>
                  <p className={styles.panelHint}>Cohort {rangeDays}d · klik een stap voor leads</p>
                </div>
              </div>
              {funnelQ.isLoading ? (
                <LoadingState />
              ) : funnel ? (
                funnel.new_c === 0 && funnel.deals_won === 0 ? (
                  <EmptyPanel
                    title="Geen leads in deze periode"
                    description="Importeer leads of wacht op inbound formulieren voor dit project."
                    actionLabel="Naar leads"
                    actionTo="/app/leads"
                  />
                ) : (
                  <div className={styles.funnelLayout}>
                    <div className={styles.funnelTrack}>
                      {funnelSteps.map((step) => {
                        const inner = (
                          <>
                            <span className={styles.funnelCount}>{step.count}</span>
                            <div>
                              <strong>{step.label}</strong>
                              <div className={styles.funnelBarWrap}>
                                <div
                                  className={styles.funnelBar}
                                  style={{
                                    width: `${funnelMax > 0 ? (step.count / funnelMax) * 100 : 0}%`,
                                  }}
                                />
                              </div>
                            </div>
                            <span className={styles.funnelMeta}>{step.pctOfNew}% van nieuw</span>
                          </>
                        );
                        return step.href ? (
                          <Link
                            key={step.key}
                            to={step.href}
                            className={`${styles.funnelStep} ${styles.funnelStepLink}`}
                          >
                            {inner}
                          </Link>
                        ) : (
                          <div key={step.key} className={styles.funnelStep}>
                            {inner}
                          </div>
                        );
                      })}
                    </div>
                    <div>
                      <h3 className={styles.panelHint}>Stap-conversie</h3>
                      <ul className={styles.conversionList}>
                        {conversions.map((c) => (
                          <li
                            key={c.label}
                            className={`${styles.conversionItem} ${c.label === dropLabel ? styles.conversionItemHighlight : ''}`}
                          >
                            <span>{c.label}</span>
                            <span className={styles.conversionPct}>{c.pct}%</span>
                          </li>
                        ))}
                      </ul>
                      {dropLabel ? (
                        <p className={styles.muted}>Grootste drop-off: {dropLabel}</p>
                      ) : null}
                    </div>
                  </div>
                )
              ) : (
                <ErrorState message="Funnel kon niet geladen worden." />
              )}
            </div>
          </section>

          <section id="campagnes" ref={campagnesView.ref} className={styles.section}>
            <div className={styles.panel}>
              <div className={styles.panelHead}>
                <div>
                  <h2 className={styles.panelTitle}>Campagnes</h2>
                  <p className={styles.panelHint}>
                    Activiteit in geselecteerde periode · sorteer op kolom
                  </p>
                </div>
              </div>
              {leaderboardQ.isLoading ? (
                <LoadingState />
              ) : (
                <SortableTable
                  columns={[
                    { key: 'name', label: 'Campagne', sortable: true },
                    { key: 'reply_rate', label: 'Reply rate', sortable: true, align: 'right' },
                    { key: 'enrolled', label: 'Enrolled', sortable: true, align: 'right' },
                    { key: 'sent', label: 'Sent', sortable: true, align: 'right' },
                  ]}
                  sort={campSort}
                  onSort={(key) => setCampSort((prev) => toggleSort(prev, key))}
                  isEmpty={sortedCampaigns.length === 0}
                  empty={
                    <EmptyPanel
                      title="Nog geen campagnedata"
                      description="Start een campagne met enrollments om reply rates te meten."
                      actionLabel="Campagnes beheren"
                      actionTo="/app/campagnes"
                    />
                  }
                >
                  {sortedCampaigns.map((c) => (
                    <tr key={c.id}>
                      <td>
                        <Link to={`/app/campagnes/${c.id}`}>{c.name}</Link>
                      </td>
                      <td className={tableStyles.num}>{formatPercent(c.reply_rate)}</td>
                      <td className={tableStyles.num}>{c.enrolled}</td>
                      <td className={tableStyles.num}>{c.sent}</td>
                    </tr>
                  ))}
                </SortableTable>
              )}
            </div>
          </section>

          <section id="pipeline" ref={pipelineView.ref} className={styles.section}>
            <div className={styles.panel}>
              <div className={styles.panelHead}>
                <div>
                  <h2 className={styles.panelTitle}>Pipeline</h2>
                  <p className={styles.panelHint}>Deals aangemaakt in periode · win rate per bron</p>
                </div>
              </div>
              {pipelineQ.isLoading ? (
                <LoadingState />
              ) : (
                <>
                  <p className={styles.muted}>
                    Median sales cycle (gewonnen):{' '}
                    <strong>
                      {pipelineQ.data?.pipeline.medianSalesCycleDays?.toFixed(1) ?? '—'}
                    </strong>{' '}
                    dagen · Open waarde{' '}
                    <strong>
                      {pipelineOpen != null ? formatEuro(pipelineOpen) : '—'}
                    </strong>
                  </p>
                  <SortableTable
                    columns={[
                      { key: 'source', label: 'Bron', sortable: true },
                      { key: 'win_rate', label: 'Win rate', sortable: true, align: 'right' },
                      { key: 'won', label: 'Gewonnen / totaal', sortable: true, align: 'right' },
                    ]}
                    sort={pipeSort}
                    onSort={(key) => setPipeSort((prev) => toggleSort(prev, key))}
                    isEmpty={sortedPipeline.length === 0}
                    empty={
                      <EmptyPanel
                        title="Nog geen pipeline-bronnen"
                        description="Koppel deals aan leads met first-touch source om win rate te zien."
                        actionLabel="Open pipeline"
                        actionTo="/app/pipeline"
                      />
                    }
                  >
                    {sortedPipeline.map((r) => (
                      <tr key={r.source}>
                        <td>{r.source || '—'}</td>
                        <td className={tableStyles.num}>{formatPercent(r.win_rate)}</td>
                        <td className={tableStyles.num}>
                          {r.won}/{r.total}
                        </td>
                      </tr>
                    ))}
                  </SortableTable>
                </>
              )}
            </div>
          </section>

          <section id="attributie" ref={attributieView.ref} className={styles.section}>
            <div className={styles.panel}>
              <div className={styles.panelHead}>
                <div>
                  <h2 className={styles.panelTitle}>Attributie</h2>
                  <p className={styles.panelHint}>
                    Leads / gewonnen deals in geselecteerde periode
                  </p>
                </div>
              </div>
              {attributionQ.isLoading ? (
                <LoadingState />
              ) : (
                <div className={styles.split}>
                  <div>
                    <h3 className={styles.panelTitle}>First-touch (leads)</h3>
                    <AttributionBars
                      rows={attributionQ.data?.attribution.first_touch ?? []}
                      emptyTitle="Geen first-touch data"
                      emptyDescription="Vul first-touch source in op leads om attributie te vullen."
                    />
                  </div>
                  <div>
                    <h3 className={styles.panelTitle}>Last-touch (gewonnen)</h3>
                    <AttributionBars
                      rows={attributionQ.data?.attribution.last_touch_won ?? []}
                      emptyTitle="Geen last-touch data"
                      emptyDescription="Gewonnen deals met last-touch source verschijnen hier."
                    />
                  </div>
                </div>
              )}
            </div>
          </section>

          <section id="doelen" ref={doelenView.ref} className={styles.section}>
            <div className={styles.panel}>
              <div className={styles.panelHead}>
                <div>
                  <h2 className={styles.panelTitle}>Doelen</h2>
                  <p className={styles.panelHint}>Status t.o.v. actuele metrics in overzicht</p>
                </div>
              </div>
              <div className={styles.goalsLayout}>
                <div>
                  {goalsQ.isLoading ? (
                    <LoadingState />
                  ) : goals.length === 0 ? (
                    <EmptyPanel
                      title="Nog geen doelen"
                      description="Stel targets in voor reply rate, bounce rate, win rate of pipeline waarde."
                    />
                  ) : (
                    <ul className={styles.conversionList}>
                      {goals.map((g) => {
                        const actual = actualForMetric(g.metric_key);
                        const target = Number(g.target_value);
                        const status = evaluateGoal(actual, target, g.direction);
                        return (
                          <li
                            key={g.id}
                            className={`${styles.goalRowItem} ${goalRowClass(status)}`}
                          >
                            <span>
                              {ANALYTICS_METRIC_LABELS[
                                g.metric_key as keyof typeof ANALYTICS_METRIC_LABELS
                              ] ?? g.metric_key}
                              <br />
                              <small>
                                Actueel {formatActual(g.metric_key, actual)} · Doel{' '}
                                {g.metric_key === 'pipeline_value'
                                  ? formatEuro(target)
                                  : formatPercent(target)}
                              </small>
                            </span>
                            <strong>{goalStatusLabel(status)}</strong>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
                <form
                  className={styles.goalForm}
                  onSubmit={(e: FormEvent) => {
                    e.preventDefault();
                    saveGoal.mutate();
                  }}
                >
                  <Field compact label="Metric">
                    <SelectInput
                      value={goalMetric}
                      onChange={(e) =>
                        setGoalMetric(e.target.value as (typeof ANALYTICS_METRIC_KEYS)[number])
                      }
                    >
                      {ANALYTICS_METRIC_KEYS.map((k) => (
                        <option key={k} value={k}>
                          {ANALYTICS_METRIC_LABELS[k]}
                        </option>
                      ))}
                    </SelectInput>
                  </Field>
                  <Field compact label="Target (decimaal, 0.05 = 5%)">
                    <TextInput value={goalTarget} onChange={(e) => setGoalTarget(e.target.value)} />
                  </Field>
                  <Button type="submit" fullWidth disabled={saveGoal.isPending}>
                    Doel opslaan
                  </Button>
                </form>
              </div>
            </div>
          </section>
        </div>
      )}
    </ProjectGate>
  );
}
